// Libs
import classNames from 'classnames/bind';
import { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import { useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogProps,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';
// Components, Layouts, Pages
import { BaseStatus, BaseTable } from '~/components/common';
// Others
import { IPayment } from '~/utils/interface/payment';
import { IOrderDetail } from '~/utils/interface/order';
import { IQuoteDetail } from '~/utils/interface/quotes';
import { IHeaderCellType } from '~/utils/interface/common';
import { formatCurrencyUSD } from '~/utils/helper/common';
import { TypeInventoryGroupDay } from '~/utils/enum/common';
// Styles, images, icons
import styles from './PaymentListModal.module.scss';
import { icons } from '~/assets';

type Props = {
  type?: string;
  orderDetail?: IOrderDetail;
  quoteDetail?: IQuoteDetail;
  onClose: () => void;
};

const cx = classNames.bind(styles);

const columnsPaymentHistory = (t: TFunction): IHeaderCellType<IPayment>[] => {
  return [
    {
      id: 'amount',
      label: t('payment_table_amount_title'),
      render: (record: IPayment) => (
        <Box className={cx('textCell')}> {formatCurrencyUSD({ value: record?.amount, showDecimal: true })}</Box>
      ),
    },
    {
      id: 'paymentMethod',
      label: t('payment_table_method_title'),
      render: (record: IPayment) => <Box className={cx('textCell')}>{record?.paymentMethod}</Box>,
    },
    {
      id: 'status',
      label: t('payment_table_status_title'),
      render: (record: IPayment) => {
        return <BaseStatus label={record?.status} />;
      },
    },
    {
      id: 'paymentDate',
      label: t('payment_table_date_title'),
      render: (record: IPayment) => <Box className={cx('textCell')}>{record?.paymentDate}</Box>,
    },
    {
      id: 'amountType',
      label: t('payment_table_amount_type_title'),
      render: (record: IPayment) => <Box className={cx('textCell')}>{record?.amountType}</Box>,
      align: 'center',
    },
    {
      id: 'note',
      label: t('payment_table_note_title'),
      render: (record: IPayment) => <Box className={cx('textCell')}>{record?.note}</Box>,
    },
  ];
};

const PaymentListModal = (props: Props) => {
  //#region Destructuring Props
  const { quoteDetail, orderDetail, type, onClose } = props;
  //#endregion Destructuring Props

  //#region Declare Hook
  const { t } = useTranslation(['admin/payment', 'common/base']);
  //#endregion Declare Hook

  //#region Selector
  //#endregion Selector

  //#region Declare State
  const [scroll] = useState<DialogProps['scroll']>('paper');
  //#endregion Declare State

  //#region Declare Memo
  //#endregion Declare Memo

  //#region Implement Hook
  //#endregion Implement Hook

  //#region Handle Function
  const handleCloseModal = () => {
    onClose && onClose();
  };
  //#endregion Handle Function

  return (
    <Dialog id='activityDetailComponent' open={true} onClose={handleCloseModal} fullWidth maxWidth={'md'}>
      <Stack className={cx('viewHeader')}>
        <Stack>
          <Typography className={cx('titleHeader')}>{t('payment_modal_title')}</Typography>
          <Typography className={cx('titleSubHeader')}>
            {t('modal_payment_sub_title', {
              name: type === TypeInventoryGroupDay.ORDER ? t('modal_payment_order') : t('modal_payment_quote'),
              value: quoteDetail?.id || orderDetail?.id,
            })}
          </Typography>
        </Stack>

        <IconButton onClick={handleCloseModal} className={cx('buttonIcon')}>
          <img src={icons.commonIconCancel} alt={t('common_img_text_alt')} className={cx('iconCancel')} />
        </IconButton>
      </Stack>

      <DialogContent dividers={scroll === 'paper'} className={cx('body')}>
        <BaseTable
          dataSource={quoteDetail?.quotePayments || orderDetail?.orderPayments || []}
          columns={columnsPaymentHistory(t)}
        />
      </DialogContent>

      <DialogActions>
        <Button variant='contained' onClick={handleCloseModal} className={cx('buttonClose')}>
          {t('common_action_close', { ns: 'common/base' })}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PaymentListModal;
